import { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingBag, Eye, MapPin, Mail, Package, XCircle, FileText } from "lucide-react";
import { ShipmentManager } from "@/components/ShipmentManager";
import { ExportMenu } from "@/components/ExportMenu";
import { useSortable, SortableTh } from "@/components/SortableTh";
import { generateInvoice } from "@/lib/invoice";

export const LIFECYCLE = ["pending", "processing", "packed", "shipped", "delivered"];

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "processing", label: "Processing" },
  { key: "packed", label: "Packed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
  { key: "cancelled", label: "Cancelled" },
];

const fState = (o) => (o.fulfillment_status || (o.status === "delivered" ? "delivered" : o.status === "shipped" ? "shipped" : "processing")).toLowerCase();

const statusBadge = (state) => {
  const cls = { pending: "bg-slate-400", processing: "bg-amber-500", packed: "bg-indigo-500", shipped: "bg-blue-500", delivered: "bg-green-600", cancelled: "bg-red-500" }[state] || "bg-amber-500";
  return <Badge className={`${cls} capitalize`}>{state}</Badge>;
};

export const OrdersManager = ({ orders = [], format, search = "", onUpdated }) => {
  const [tab, setTab] = useState("all");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => tab === "all" || fState(o) === tab).filter((o) =>
      !q || o.order_id.toLowerCase().includes(q) || (o.customer_name || "").toLowerCase().includes(q) || (o.customer_email || "").toLowerCase().includes(q) || (o.items || []).some((it) => (it.title || "").toLowerCase().includes(q))
    );
  }, [orders, tab, search]);

  const { sorted, sortKey, dir, toggle } = useSortable(filtered, {
    order_id: (o) => o.order_id,
    customer: (o) => o.customer_name || "",
    items: (o) => (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0),
    total: (o) => o.seller_total ?? 0,
    status: (o) => fState(o),
    date: (o) => o.created_at,
  }, "date");

  const counts = useMemo(() => {
    const c = { all: orders.length };
    STATUS_TABS.slice(1).forEach((t) => { c[t.key] = orders.filter((o) => fState(o) === t.key).length; });
    return c;
  }, [orders]);

  const setStatus = async (order, status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/fulfillment`, { fulfillment_status: status });
      toast.success(`Order marked ${status}`);
      setSelected((s) => (s ? { ...s, ...data } : s));
      onUpdated?.(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update status"); }
    finally { setBusy(false); }
  };

  const exportCols = [
    { header: "Order", accessor: (o) => `#${o.order_id.slice(-8)}` },
    { header: "Date", accessor: (o) => new Date(o.created_at).toLocaleDateString() },
    { header: "Customer", accessor: (o) => o.customer_name },
    { header: "Email", accessor: (o) => o.customer_email },
    { header: "Items", accessor: (o) => (o.items || []).reduce((s, it) => s + (it.quantity || 1), 0) },
    { header: "Total (EUR)", accessor: (o) => (o.seller_total ?? 0).toFixed(2) },
    { header: "Status", accessor: (o) => fState(o) },
  ];

  const addr = selected?.shipping_address;
  const cur = selected ? fState(selected) : "processing";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="orders-status-tabs">
            {STATUS_TABS.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`orders-tab-${t.key}`}>
                {t.label} <span className="ml-1.5 text-xs opacity-60">{counts[t.key] ?? 0}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <ExportMenu filename="orders" title="Orders" columns={exportCols} rows={filtered} testid="orders-export" />
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTh label="Order" sortKey="order_id" activeKey={sortKey} dir={dir} onSort={toggle} />
              <SortableTh label="Customer" sortKey="customer" activeKey={sortKey} dir={dir} onSort={toggle} />
              <SortableTh label="Items" sortKey="items" activeKey={sortKey} dir={dir} onSort={toggle} className="hidden md:table-cell" />
              <SortableTh label="Total" sortKey="total" activeKey={sortKey} dir={dir} onSort={toggle} />
              <SortableTh label="Status" sortKey="status" activeKey={sortKey} dir={dir} onSort={toggle} />
              <TableHead className="text-right">Fulfil</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((o) => (
              <TableRow key={o.order_id} data-testid={`order-row-${o.order_id}`}>
                <TableCell>
                  <p className="font-mono text-xs font-semibold">#{o.order_id.slice(-8)}</p>
                  <p className="text-[11px] text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</p>
                </TableCell>
                <TableCell>
                  <p className="text-sm font-medium truncate max-w-[160px]">{o.customer_name}</p>
                  <p className="text-[11px] text-muted-foreground truncate max-w-[160px]">{o.customer_email}</p>
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{(o.items || []).reduce((s, it) => s + (it.quantity || 1), 0)} item(s)</TableCell>
                <TableCell className="text-sm font-semibold">{format(o.seller_total ?? 0)}</TableCell>
                <TableCell>{statusBadge(fState(o))}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" className="rounded-full" data-testid={`view-order-${o.order_id}`} onClick={() => setSelected(o)}>
                    <Eye className="w-4 h-4 mr-1" />Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {orders.length === 0 ? "No paid orders yet." : "No orders match this filter."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="order-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-mono">Order #{selected.order_id.slice(-8)}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-2">
                  {statusBadge(cur)}
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`invoice-${selected.order_id}`}
                      onClick={() => generateInvoice(selected)}>
                      <FileText className="w-4 h-4 mr-1" />Invoice
                    </Button>
                    <span className="text-lg font-black font-display">{format(selected.seller_total ?? 0)}</span>
                  </div>
                </div>

                {/* Lifecycle control */}
                <div className="rounded-xl border p-3">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Order status</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="fulfillment-controls">
                    {LIFECYCLE.map((st) => {
                      const idx = LIFECYCLE.indexOf(st);
                      const curIdx = LIFECYCLE.indexOf(cur);
                      const done = curIdx >= idx && cur !== "cancelled";
                      return (
                        <Button key={st} size="sm" disabled={busy || cur === "cancelled" || st === cur}
                          variant={done ? "default" : "outline"}
                          className="rounded-full capitalize text-xs h-8"
                          data-testid={`set-status-${st}`} onClick={() => setStatus(selected, st)}>
                          {st}
                        </Button>
                      );
                    })}
                    <Button size="sm" variant="outline" disabled={busy || cur === "cancelled" || cur === "delivered"}
                      className="rounded-full text-xs h-8 text-red-600 border-red-200 hover:bg-red-50"
                      data-testid="set-status-cancelled" onClick={() => setStatus(selected, "cancelled")}>
                      <XCircle className="w-3.5 h-3.5 mr-1" />Cancel
                    </Button>
                  </div>
                </div>

                <div className="rounded-xl border p-3 space-y-1 text-sm">
                  <p className="font-medium flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{selected.customer_name}</p>
                  <p className="text-muted-foreground text-xs">{selected.customer_email}</p>
                  {addr && (
                    <p className="text-xs text-muted-foreground flex items-start gap-1.5 pt-1">
                      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <span>{addr.full_name}, {addr.line1}, {addr.city}, {addr.postal_code}, {addr.country}{addr.phone ? ` · ${addr.phone}` : ""}</span>
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Items</p>
                  {selected.items.map((it, i) => (
                    <div key={`${it.product_id}-${i}`} className="flex items-center gap-3 text-sm">
                      <div className="w-10 h-10 rounded-lg bg-muted overflow-hidden shrink-0 flex items-center justify-center">
                        {it.image ? <img src={it.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <span className="flex-1 truncate">{it.title}</span>
                      <span className="text-muted-foreground">×{it.quantity}</span>
                      <span className="font-medium w-16 text-right">{format((it.price || 0) * (it.quantity || 1))}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-2 border-t">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Carrier & tracking</p>
                  <ShipmentManager order={selected} onUpdated={(updated) => { onUpdated?.(updated); if (updated) setSelected((s) => ({ ...s, ...updated })); }} />
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
